import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FieldMetaData, GbpersonService, PersonGbSearchErweitertRequestMeta } from '../../../../core/gb-api';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { Subscription } from 'rxjs';
import { submit } from '../../persongb.functions';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { SearchStateService } from '../../../../shared/services/search-state.service';
import { SearchMode } from '../../../../shared/enum/searchMode';
import { copyMetaData } from '../../../../shared/functions/meta.functions';
import { CapFormColumnsDirective } from '../../../../shared/form-control/form-columns.directive';
import { MetaDirective } from '../../../../shared/directives/meta.directive';
import { CapFormControlComponent } from '../../../../shared/form-control/form-control.component';
import { MetaDisablerDirective } from '../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../shared/directives/meta-validator.directive';
import { TeilDatumComponent } from '../../../../shared/widgets/teil-datum/teil-datum.component';
import { CapFormRowDirective } from '../../../../shared/form-control/cap-form-row.directive';
import { HistoryOptionsComponent } from '../../../../shared/widgets/history-options/history-options.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import AccessEnum = FieldMetaData.AccessEnum;

@Component({
  selector: 'cap-persongb-suche-erweitert',
  templateUrl: './persongb-suche-erweitert.component.html',
  styleUrls: ['./persongb-suche-erweitert.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    MetaDirective,
    CapFormControlComponent,
    MetaDisablerDirective,
    MetaValidatorDirective,
    TeilDatumComponent,
    CapFormRowDirective,
    HistoryOptionsComponent,
    NgbTooltip,
    TranslatePipe
  ]
})
export class PersongbSucheErweitertComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('form', { static: false })
  form: NgForm;

  @ViewChild('name', { static: false })
  nameField: ElementRef;

  erweitert: PersonGbSearchErweitertRequestMeta;
  personstammArten: Array<PersonGbSearchErweitertRequestMeta.MPersonStammVersionArtListEnum> = [null];

  eigentuemerActiv = false;
  uebrigeBerechtigteActiv = false;
  glaeubigerSchuldnerActiv = false;

  eigentuemerDisabled = true;
  uebrigeBerechtigteDisabled = true;
  glaeubigerSchuldnerDisabled = true;

  codeGlaeubigerDisabled: boolean;
  alleRollen = true;
  alleUebrigeBerechtigte;
  private subscription: Subscription;

  constructor(
    private stateService: SearchStateService,
    private personService: GbpersonService,
    private route: ActivatedRoute,
    private router: Router,
    private timepointService: TimepointService,
    public formatConfig: FormatConfigService
  ) {
    keysToTranslate(['ACI', 'RF', 'GeburtsdatumJahr', 'GeburtsdatumMonat', 'GeburtsdatumTag', 'Geburtsdatum', 'unscharfeSuche']);
    keysToTranslate<string>([
      'ALLE_PERSON',
      'NATUERLICHE_PERSON',
      'JURISTISCHE_PERSON',
      'SCHWEIZERISCHE_JURISTISCHE_PERSON',
      'OEFFENTLICHE_KOERPERSCHAFT',
      'AUSLAENDISCHE_RECHTSFORM',
      'GEMEINSCHAFT',
      'EIGENTUEMER',
      'SCHULDNER',
      'UEBRIGE_BERECHTIGTE',
      'GLAEUBIGER_SCHULDNER',
      'GRUNDPFANDGLAEUBIGER',
      'FAUSTPFANDGLAEUBIGER',
      'NUTZNIESSER',
      'ANDEREROLLE',
      'WOHNADRESSE',
      'RECHTSDOMIZIL',
      'ZUSTELLADRESSE',
      'VERWALTERADRESSE',
      'ANDEREADRESSE',
      'RECHNUNGSADRESSE',
      'REFERENZADRESSE'
    ]);
  }

  ngOnInit() {
    this.subscription = this.personService.searchPersonGbErweitertTemplate().subscribe((template) => {
      this.erweitert = this.copyLastRequest(template, SearchMode.searchErweitert);
      this.erweitert.mPersonStammVersionArtList.forEach((a) => this.personstammArten.push(a));
      this.updateElasticSearchFlags();
      this.activateRolle();
      this.disableCodeGlaeubiger();
    });
  }

  ngAfterViewInit() {
    this.setFocusToFirstField();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  private setFocusToFirstField() {
    this.nameField.nativeElement.focus();
  }

  private activateRolle() {
    this.subscription = this.personService.searchPersonGbErweitertTemplate().subscribe((template) => {
      this.eigentuemerDisabled = !template.isSearchEigentuemerEnabled;
      this.uebrigeBerechtigteDisabled = !template.isSearchUebrigeBerechtigteEnabled;
      this.glaeubigerSchuldnerDisabled = !template.isSearchGlaeubigerSchuldnerEnabled;

      // Wenn sich Rechte aendern und alte Lesezeichen verwendet werden, koennen wir uns nicht
      // auf die alte Suche verlassen, sondern muessen uns nach dem aktuellen Template richten
      if (template.isSearchEigentuemerEnabled) {
        this.eigentuemerActiv = this.erweitert.rolle === 'EIGENTUEMER';
      } else {
        if (this.erweitert.rolle === 'EIGENTUEMER') {
          this.onReset();
        }
      }
      if (template.isSearchUebrigeBerechtigteEnabled) {
        this.uebrigeBerechtigteActiv = this.erweitert.rolle === 'UEBRIGE_BERECHTIGTE';
      } else {
        if (this.erweitert.rolle === 'UEBRIGE_BERECHTIGTE') {
          this.onReset();
        }
      }
      if (template.isSearchGlaeubigerSchuldnerEnabled) {
        this.glaeubigerSchuldnerActiv = this.erweitert.rolle === 'GLAEUBIGER_SCHULDNER';
      } else {
        if (this.erweitert.rolle === 'GLAEUBIGER_SCHULDNER') {
          this.onReset();
        }
      }
    });
  }

  onSubmit(): void {
    this.timepointService.resetTimepoint();
    this.stateService.lastRequest = this.erweitert;
    this.stateService.lastSearchMode = SearchMode.searchErweitert;
    submit(this.form, SearchMode.searchErweitert, this.erweitert, this.router, this.route, '../person/list');
  }

  onReset() {
    this.form.reset();
    this.subscription = this.personService.searchPersonGbErweitertTemplate().subscribe((template) => {
      this.erweitert = template;
      this.updateElasticSearchFlags();
      this.activateRolle();
      this.setFocusToFirstField();
    });
  }

  onClickRolle($event) {
    if (!this.erweitert) {
      return;
    }
    switch ($event.target.id) {
      case 'eigentuemer':
        this.erweitert.rolle = 'EIGENTUEMER';
        break;
      case 'uebrigeBerechtigte':
        this.erweitert.rolle = 'UEBRIGE_BERECHTIGTE';
        break;
      case 'glaeubigerSchuldner':
        this.erweitert.rolle = 'GLAEUBIGER_SCHULDNER';
        break;
      default:
        throw new Error($event.target.id + ' is not supported');
    }
    this.activateRolle();
  }

  disableCodeGlaeubiger() {
    if (this.erweitert.glaeubigerrolle === 'ANDEREROLLE') {
      this.codeGlaeubigerDisabled = false;
    } else {
      this.codeGlaeubigerDisabled = true;
      this.erweitert.codeGlaeubiger = null;
    }
  }

  updateAlle($event) {
    if ($event.target.checked) {
      if (this.erweitert.mRolleEigentuemer.access !== AccessEnum.NONE) {
        this.erweitert.rolleEigentuemer = true;
      }
      if (this.erweitert && this.erweitert.isSearchUebrigeBerechtigteEnabled) {
        this.alleUebrigeBerechtigte = true;
        this.updateUebrigeBerechtigte($event);
      }
      if (this.erweitert.mRolleGlaeubiger.access !== AccessEnum.NONE) {
        this.erweitert.rolleGlaeubiger = true;
      }
      if (this.erweitert.mRolleSchuldner.access !== AccessEnum.NONE) {
        this.erweitert.rolleSchuldner = true;
      }
    }
  }

  updateUebrigeBerechtigte($event): void {
    if (this.erweitert.mRolleUebrigeBerechtigeAnmerkung.access !== AccessEnum.NONE) {
      this.erweitert.rolleUebrigeBerechtigeAnmerkung = $event.target.checked;
    }
    if (this.erweitert.mRolleUebrigeBerechtigeDienstbarkeit.access !== AccessEnum.NONE) {
      this.erweitert.rolleUebrigeBerechtigeDienstbarkeit = $event.target.checked;
    }
    if (this.erweitert.mRolleUebrigeBerechtigeGrundlast.access !== AccessEnum.NONE) {
      this.erweitert.rolleUebrigeBerechtigeGrundlast = $event.target.checked;
    }
    if (this.erweitert.mRolleUebrigeBerechtigeVormerkung.access !== AccessEnum.NONE) {
      this.erweitert.rolleUebrigeBerechtigeVormerkung = $event.target.checked;
    }
  }

  updateElasticSearchFlags() {
    const alleUebrigeBerechtigteFields =
      (this.erweitert.rolleUebrigeBerechtigeAnmerkung || this.erweitert.mRolleUebrigeBerechtigeAnmerkung.access === AccessEnum.NONE) &&
      (this.erweitert.rolleUebrigeBerechtigeDienstbarkeit ||
        this.erweitert.mRolleUebrigeBerechtigeDienstbarkeit.access === AccessEnum.NONE) &&
      (this.erweitert.rolleUebrigeBerechtigeGrundlast || this.erweitert.mRolleUebrigeBerechtigeGrundlast.access === AccessEnum.NONE) &&
      (this.erweitert.rolleUebrigeBerechtigeVormerkung || this.erweitert.mRolleUebrigeBerechtigeVormerkung.access === AccessEnum.NONE);
    this.alleUebrigeBerechtigte =
      alleUebrigeBerechtigteFields &&
      (this.erweitert.mRolleUebrigeBerechtigeAnmerkung.access === AccessEnum.ALLOWED ||
        this.erweitert.mRolleUebrigeBerechtigeDienstbarkeit.access === AccessEnum.ALLOWED ||
        this.erweitert.mRolleUebrigeBerechtigeGrundlast.access === AccessEnum.ALLOWED ||
        this.erweitert.mRolleUebrigeBerechtigeVormerkung.access === AccessEnum.ALLOWED);
    this.alleRollen =
      (this.erweitert.rolleEigentuemer || this.erweitert.mRolleEigentuemer.access === AccessEnum.NONE) &&
      alleUebrigeBerechtigteFields &&
      (this.erweitert.rolleGlaeubiger || this.erweitert.mRolleGlaeubiger.access === AccessEnum.NONE) &&
      (this.erweitert.rolleSchuldner || this.erweitert.mRolleSchuldner.access === AccessEnum.NONE);
  }

  copyLastRequest(template, searchmode: SearchMode = SearchMode.searchStandard) {
    {
      if (!this.stateService.lastSearchMode || (this.stateService.lastSearchMode && this.stateService.lastSearchMode === searchmode)) {
        if (this.stateService.lastRequest) {
          return copyMetaData(template, this.stateService.lastRequest);
        }
      }
      return template;
    }
  }
}
